import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useEffect, useRef } from 'react';

import { AUTH_TOKEN } from 'src/common/constants';
import { useStableCallback } from './useStableCallback';

const SSE_RETRY_INTERVAL_MS = 3000;
const SSE_CONNECT_DEBOUNCE_MS = 5000;

export class SseHttpError extends Error {
  constructor(public readonly status: number) {
    super(`[SSE] Falha na conexão: ${status}`);
  }
}

interface UsePrintingSSEParams {
  isEnabled: boolean;
  distributionCenterId: string | undefined;
  pcId: number;
  onPrintZpl: (zpl: string) => Promise<void>;
  onError: (error: Error) => void;
  onConnecting?: () => void;
  onConnected?: () => void;
  onConnectionError?: (error: Error) => void;
  onDisconnected?: (error: Error) => void;
  onPrinting?: (tagCode: string) => void;
  onAbort?: () => void;
}

export function usePrintingSSE({
  isEnabled,
  distributionCenterId,
  pcId,
  onPrintZpl,
  onError,
  onConnecting,
  onConnected,
  onConnectionError,
  onDisconnected,
  onPrinting,
  onAbort,
}: UsePrintingSSEParams): void {
  const hasConnectedRef = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);
  const stableOnConnecting = useStableCallback(onConnecting);
  const stableOnConnected = useStableCallback(onConnected);
  const stableOnConnectionError = useStableCallback(onConnectionError);
  const stableOnDisconnected = useStableCallback(onDisconnected);
  const stableOnPrinting = useStableCallback(onPrinting);
  const stableOnPrintZpl = useStableCallback(onPrintZpl);
  const stableOnError = useStableCallback(onError);
  const stableOnAbort = useStableCallback(onAbort);

  useEffect(() => {
    if (!isEnabled || !distributionCenterId || !pcId) {
      return undefined;
    }

    hasConnectedRef.current = false;

    const buildUrl = (dcId: string, pc: number): string => {
      const url = new URL('/sse/printing', process.env.DOBBY_API);
      url.searchParams.set('distributionCenterId', dcId);
      url.searchParams.set('pcId', String(pc));
      return url.toString();
    };

    const handleOpen = async (response: Response): Promise<void> => {
      if (!response.ok) {
        throw new SseHttpError(response.status);
      }
      hasConnectedRef.current = true;
      stableOnConnected();
    };

    const handlePrintTag = (data: string): void => {
      const { zpl, tagCode } = JSON.parse(data);
      stableOnPrinting(tagCode);
      stableOnPrintZpl(zpl).catch(stableOnError);
    };

    const handleMessage = (event: any): void => {
      switch (event.event) {
        case 'printTag': {
          return handlePrintTag(event.data);
        }
      }
    };

    const handleConnectionClose = (): never => {
      throw new Error('[SSE] Conexão encerrada pelo servidor');
    };

    const handleError = (err: any): number | void => {
      const wasConnected = hasConnectedRef.current;
      hasConnectedRef.current = false;
      if (wasConnected) {
        stableOnDisconnected(err);
      }
      stableOnConnectionError(err);

      if (err instanceof SseHttpError && err.status === 401) {
        throw err;
      }

      if (err instanceof SseHttpError && err.status === 409) {
        throw err;
      }

      stableOnConnecting();
      return SSE_RETRY_INTERVAL_MS;
    };

    stableOnConnecting();
    const timeoutId = setTimeout(() => {
      const token = localStorage.getItem(AUTH_TOKEN);
      const sseUrl = buildUrl(distributionCenterId, pcId);
      controllerRef.current = new AbortController();

      fetchEventSource(sseUrl, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controllerRef.current.signal,
        openWhenHidden: true,
        onopen: handleOpen,
        onmessage: handleMessage,
        onerror: handleError,
        onclose: handleConnectionClose,
      });
    }, SSE_CONNECT_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      if (!hasConnectedRef.current) {
        stableOnAbort();
      }
    };
  }, [
    isEnabled,
    distributionCenterId,
    pcId,
    stableOnConnecting,
    stableOnConnected,
    stableOnConnectionError,
    stableOnDisconnected,
    stableOnPrinting,
    stableOnPrintZpl,
    stableOnError,
    stableOnAbort,
  ]);
}
